/**
 * BigCommerce Provider Adapter — Phase 5
 *
 * Translates canonical Channel Listing data into BigCommerce API payload shapes.
 * Models BigCommerce-specific concepts: products, variants, options, modifiers,
 * categories, brands, custom fields, images, inventory.
 *
 * Honest modes: NOT_CONFIGURED / DRY_RUN / LIVE
 * No fake provider-confirmed state. No simulation masquerading as live.
 */

import type { ChannelListing, ChannelListingVariant } from "@/lib/channels";
import {
  parseBcPublicationProfile,
  resolveBcCategories,
  resolveBcIsVisible,
  type BcPublicationProfile,
} from "./bigcommerce-catalogue-safety";
import { buildBcCatalogMetadata } from "./bigcommerce-storefront-payload";
import { resolveShippingFacts } from "@/lib/shipping-facts";
import { filterProviderCustomFields, pickProductForProvider } from "./provider-outbound-allowlist";

// ---------------------------------------------------------------------------
// BigCommerce Payload Types
// ---------------------------------------------------------------------------

export interface BigCommerceProductPayload {
  name: string;
  type: "physical" | "digital";
  description: string;
  price: number;
  sku?: string;
  brand_name?: string;
  mpn?: string;
  gtin?: string;
  upc?: string;
  weight: number;
  width?: number;
  height?: number;
  depth?: number;
  is_visible: boolean;
  is_featured?: boolean;
  page_title?: string;
  meta_description?: string;
  search_keywords?: string;
  meta_keywords?: string[];
  custom_url?: { url: string; is_customized: true };
  condition?: "New" | "Used" | "Refurbished";
  is_condition_shown?: boolean;
  open_graph_type?: "product";
  open_graph_title?: string;
  open_graph_description?: string;
  open_graph_use_meta_description?: boolean;
  open_graph_use_product_name?: boolean;
  open_graph_use_image?: boolean;
  order_quantity_minimum?: number;
  order_quantity_maximum?: number;
  categories: number[];
  images: BigCommerceImagePayload[];
  variants: BigCommerceVariantPayload[];
  custom_fields: BigCommerceCustomFieldPayload[];
  option_values?: BigCommerceOptionValuePayload[];
}

export interface BigCommerceVariantPayload {
  sku: string;
  price: number;
  barcode?: string;
  mpn?: string;
  gtin?: string;
  upc?: string;
  weight?: number;
  option_values: { option_display_name: string; label: string }[];
  image_url?: string;
}

export interface BigCommerceImagePayload {
  image_url: string;
  is_thumbnail: boolean;
  sort_order: number;
  description: string;
}

export interface BigCommerceCustomFieldPayload {
  name: string;
  value: string;
}

export interface BigCommerceOptionValuePayload {
  option_display_name: string;
  label: string;
}

export interface BigCommerceCategoryPayload {
  id: number;
  parent_id?: number;
  name: string;
  path?: string;
  is_visible: boolean;
}

export interface BigCommerceBrandPayload {
  id: number;
  name: string;
}

// ---------------------------------------------------------------------------
// Honest Mode Classification
// ---------------------------------------------------------------------------

export type BcHonestMode = "NOT_CONFIGURED" | "DRY_RUN" | "LIVE";

export function classifyBcMode(config?: {
  app_credentials_status?: string;
  auth_status?: string;
}): BcHonestMode {
  if (!config || config.app_credentials_status === "not_configured") return "NOT_CONFIGURED";
  // connector_bigcommerce_config.auth_status CHECK allows authorized, not "connected".
  if (config.auth_status === "connected" || config.auth_status === "authorized") return "LIVE";
  return "DRY_RUN";
}

// ---------------------------------------------------------------------------
// Product Payload Builder
// ---------------------------------------------------------------------------

export interface BuildBcProductParams {
  listing: ChannelListing;
  product: {
    name: string;
    description: string | null;
    sku: string | null;
    brand: string | null;
    mpn: string | null;
    gtin: string | null;
    upc: string | null;
    weight_kg: number | null;
    length_cm: number | null;
    width_cm: number | null;
    height_cm: number | null;
    selling_price: number | null;
    condition: string | null;
    seo_title?: string | null;
    meta_description?: string | null;
    seo_keywords?: string | null;
    url_slug?: string | null;
    og_title?: string | null;
    og_description?: string | null;
    og_image_url?: string | null;
    storefront_visible?: boolean | null;
    is_featured?: boolean | null;
    min_purchase_qty?: number | null;
    max_purchase_qty?: number | null;
  };
  variants: ChannelListingVariant[];
  categoryIds: number[];
  imageUrls: { url: string; isPrimary: boolean; altText: string; sortOrder: number; variantId?: string | null }[];
  customFields: { name: string; value: string }[];
  identifiers: Array<{ identifier_type: string; identifier_value: string }>;
  identifierMode?: "omitted" | "does_not_apply" | "provided";
  /** Default production (visible). Certification is hidden via catalogue safety. */
  publicationProfile?: BcPublicationProfile;
}

export function buildBigCommerceProductPayload(params: BuildBcProductParams): BigCommerceProductPayload {
  const { listing, variants, categoryIds, imageUrls, identifiers } = params;
  const product = pickProductForProvider("bigcommerce", params.product as unknown as Record<string, unknown>) as typeof params.product;
  const customFields = filterProviderCustomFields(params.customFields);
  const publicationProfile = parseBcPublicationProfile(params.publicationProfile);

  const includedVariants = variants.filter((v) => v.included);
  const isVariation = includedVariants.length > 0;

  const mpn = identifiers.find((i) => i.identifier_type === "mpn")?.identifier_value || product.mpn || undefined;

  // Build variant payloads
  const bcVariants: BigCommerceVariantPayload[] = isVariation
    ? includedVariants.map((v) => {
        const price =
          listing.price_strategy === "inherit"
            ? (v.variant?.selling_price ?? product.selling_price ?? 0)
            : (v.effective_channel_price ?? v.channel_price ?? v.variant?.selling_price ?? product.selling_price ?? 0);

        const optionValues = Object.entries(v.variant?.option_values ?? {}).map(([k, val]) => ({
          option_display_name: k,
          label: val,
        }));

        // Find variant-specific image if any
        const variantImage = imageUrls.find((img) => img.variantId === v.variant_id);

        const shipping = resolveShippingFacts(
          {
            weightKg: v.variant?.weight_kg,
            lengthCm: v.variant?.length_cm,
            widthCm: v.variant?.width_cm,
            heightCm: v.variant?.height_cm,
          },
          {
            weightKg: product.weight_kg,
            lengthCm: product.length_cm,
            widthCm: product.width_cm,
            heightCm: product.height_cm,
          },
        );

        return {
          sku: v.channel_sku || v.variant?.sku || "",
          price: Number(price ?? 0),
          barcode: v.channel_barcode || v.variant?.barcode || undefined,
          mpn: mpn,
          weight: shipping.weightKg ?? undefined,
          option_values: optionValues,
          image_url: variantImage?.url,
        };
      })
    : [];

  // Build image payloads
  const images: BigCommerceImagePayload[] = imageUrls.map((img) => ({
    image_url: img.url,
    is_thumbnail: img.isPrimary,
    sort_order: img.sortOrder,
    description: img.altText,
  }));

  const catalogMeta = buildBcCatalogMetadata({
    product,
    listing: listing as TradivoListingSeo,
    identifiers,
    identifierPolicy: { mode: params.identifierMode ?? "does_not_apply" },
    hasProductImage: images.length > 0,
    forceHidden: !resolveBcIsVisible(publicationProfile),
  });

  const productShipping = resolveShippingFacts(null, {
    weightKg: product.weight_kg,
    lengthCm: product.length_cm,
    widthCm: product.width_cm,
    heightCm: product.height_cm,
  });

  return {
    name: listing.channel_title || product.name,
    type: "physical",
    description: listing.channel_description || product.description || "",
    price: Number(product.selling_price ?? 0),
    sku: product.sku ?? undefined,
    weight: Number(productShipping.weightKg ?? 0),
    width: productShipping.widthCm ?? undefined,
    height: productShipping.heightCm ?? undefined,
    depth: productShipping.lengthCm ?? undefined,
    categories: resolveBcCategories(publicationProfile, categoryIds),
    images,
    variants: bcVariants,
    custom_fields: customFields,
    ...catalogMeta,
    is_visible: catalogMeta.is_visible ?? resolveBcIsVisible(publicationProfile),
  };
}

type TradivoListingSeo = {
  channel_seo_title?: string | null;
  channel_meta_description?: string | null;
  channel_seo_keywords?: string | null;
  channel_url_slug?: string | null;
  channel_og_title?: string | null;
  channel_og_description?: string | null;
  channel_storefront_visible?: boolean | null;
  channel_is_featured?: boolean | null;
};

// ---------------------------------------------------------------------------
// Option Mapping Helpers
// ---------------------------------------------------------------------------

export function buildBigCommerceOptionMapping(
  canonicalOptions: string[],
  userMappings: Record<string, string>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const opt of canonicalOptions) {
    out[opt] = userMappings[opt] ?? opt;
  }
  return out;
}

export function extractCanonicalOptionsFromVariants(variants: ChannelListingVariant[]): string[] {
  if (!variants || variants.length === 0) return [];
  const first = variants.find((v) => v.included && v.variant?.option_values);
  if (!first) return [];
  return Object.keys(first.variant?.option_values ?? {});
}

// ---------------------------------------------------------------------------
// Category/Brand Helpers
// ---------------------------------------------------------------------------

export function buildBcCategoryPayloads(
  categories: Array<{ id: number; parent_id?: number; name: string; path?: string; is_visible?: boolean }>,
): BigCommerceCategoryPayload[] {
  return categories.map((c) => ({
    id: c.id,
    parent_id: c.parent_id,
    name: c.name,
    path: c.path,
    is_visible: c.is_visible ?? true,
  }));
}

export function resolveBcBrandId(
  brandName: string | null,
  brandCache: Array<{ external_brand_id: number; name: string }>,
): number | undefined {
  if (!brandName) return undefined;
  const found = brandCache.find((b) => b.name.toLowerCase() === brandName.toLowerCase());
  return found?.external_brand_id;
}

// ---------------------------------------------------------------------------
// Inventory Helpers
// ---------------------------------------------------------------------------

export interface BcInventoryPayload {
  sku: string;
  inventory_level: number;
  inventory_tracking: "product" | "variant";
}

export function buildBcInventoryPayload(
  sku: string,
  availableQty: number,
  isVariant: boolean,
): BcInventoryPayload {
  return {
    sku,
    inventory_level: Math.max(0, availableQty),
    inventory_tracking: isVariant ? "variant" : "product",
  };
}

// ---------------------------------------------------------------------------
// Error Normalization
// ---------------------------------------------------------------------------

export interface BcNormalizedError {
  class: string;
  retryable: boolean;
  message: string;
}

export function normalizeBcError(status: number, message: string): BcNormalizedError {
  if (status === 401) return { class: "authentication", retryable: false, message };
  if (status === 403) return { class: "authorization", retryable: false, message };
  if (status === 404) return { class: "not_found", retryable: false, message };
  if (status === 409) return { class: "conflict", retryable: false, message };
  if (status === 422) return { class: "validation", retryable: false, message };
  if (status === 429) return { class: "rate_limit", retryable: true, message };
  if (status >= 500) return { class: "temporary_provider", retryable: true, message };
  if (status === 0) return { class: "network", retryable: true, message };
  return { class: "permanent_provider", retryable: false, message };
}

// ---------------------------------------------------------------------------
// Reconciliation
// ---------------------------------------------------------------------------

export interface BcReconciliationEntry {
  field: string;
  internal: string | number | boolean | null;
  external: string | number | boolean | null;
  status: "match" | "drift" | "missing_external" | "missing_internal";
}

export function reconcileBigCommerceProduct(
  internal: { name: string; price: number | null; is_visible: boolean; sku?: string | null },
  external: { name: string | null; price: number | null; is_visible: boolean | null; sku?: string | null } | null,
): BcReconciliationEntry[] {
  if (!external) {
    return [
      { field: "name", internal: internal.name, external: null, status: "missing_external" },
      { field: "price", internal: internal.price, external: null, status: "missing_external" },
      { field: "is_visible", internal: internal.is_visible, external: null, status: "missing_external" },
    ];
  }
  return [
    {
      field: "name",
      internal: internal.name,
      external: external.name,
      status: external.name == null ? "missing_external" : internal.name === external.name ? "match" : "drift",
    },
    {
      field: "price",
      internal: internal.price,
      external: external.price,
      status: external.price == null ? "missing_external" : Math.abs((internal.price ?? 0) - external.price) < 0.01 ? "match" : "drift",
    },
    {
      field: "is_visible",
      internal: internal.is_visible,
      external: external.is_visible,
      status: external.is_visible == null ? "missing_external" : internal.is_visible === external.is_visible ? "match" : "drift",
    },
    {
      field: "sku",
      internal: internal.sku ?? null,
      external: external.sku ?? null,
      status: (external.sku ?? null) == null ? "missing_external" : internal.sku === external.sku ? "match" : "drift",
    },
  ];
}

// ---------------------------------------------------------------------------
// Publication Plan Builder
// ---------------------------------------------------------------------------

export interface BcPublicationStep {
  step: number;
  name: string;
  skipped: boolean;
  count?: number;
}

export function buildBcPublicationPlan(isVariation: boolean, variantCount: number): BcPublicationStep[] {
  const steps: BcPublicationStep[] = [
    { step: 1, name: "Validate generic listing readiness", skipped: false },
    { step: 2, name: "Validate BigCommerce-specific readiness", skipped: false },
    { step: 3, name: "Load provider-neutral publication projection", skipped: false },
    { step: 4, name: "Resolve channel/storefront", skipped: false },
    { step: 5, name: "Resolve categories", skipped: false },
    { step: 6, name: "Resolve brand", skipped: false },
    { step: 7, name: "Resolve product attributes/custom fields", skipped: false },
    { step: 8, name: "Resolve product media", skipped: false },
    { step: 9, name: "Create/update BigCommerce product", skipped: false },
    { step: 10, name: "Create/update options", skipped: !isVariation, count: isVariation ? undefined : 0 },
    { step: 11, name: "Create/update variants", skipped: !isVariation, count: isVariation ? variantCount : 0 },
    { step: 12, name: "Apply images", skipped: false },
    { step: 13, name: "Apply inventory", skipped: false },
    { step: 14, name: "Persist actual provider IDs", skipped: false },
    { step: 15, name: "Record publication snapshot", skipped: false },
    { step: 16, name: "Mark provider-confirmed state", skipped: false },
    { step: 17, name: "Clear successfully synchronized dirty domains", skipped: false },
  ];
  return steps;
}

// ---------------------------------------------------------------------------
// Retry Policy
// ---------------------------------------------------------------------------

export function shouldRetryBc(errorClass: string): boolean {
  const retryable = new Set(["rate_limit", "temporary_provider", "network"]);
  return retryable.has(errorClass);
}

export function bcBackoffMs(attempt: number): number {
  return Math.min(1000 * Math.pow(2, attempt), 30000);
}

// ---------------------------------------------------------------------------
// Idempotency
// ---------------------------------------------------------------------------

export function buildBcIdempotencyKey(listingId: string, version: number, operation: string): string {
  return `${listingId}:${version}:${operation}`;
}